/**
 * @fileoverview `/site` and `/site/:naddr` -- making the thing that turns a pile
 * of pages into a site.
 *
 * A THIRD WRITING ROUTE, AND THE GATE IS INSIDE IT for the same reason it is
 * inside the page editor and the theme editor: this app's main screen is a
 * public page, so a gate around the app would put a sign-in message in front of
 * every reader with no account, on the one route the product exists for.
 *
 * THE EDIT URL HOLDS THE SITE'S ADDRESS, not an event id, because an id would
 * break the first time the site was saved -- the one moment the link is
 * guaranteed to be used again.
 *
 * ---
 *
 * THE KEY IS RECOVERED BEFORE THE FORM OPENS, AND THAT IS THIS FILE'S REASON TO
 * EXIST.
 *
 * The publisher refuses an edit that arrives without the collection's key,
 * because writing under a fresh one silently revokes every recipient. That
 * refusal is correct and, on its own, cruel: it lands after the author has
 * arranged forty pages, and the arranging is what they lose. So the key is
 * unwrapped from the owner's envelope here, before the editor is handed
 * anything, and a site whose key will not open is refused on a screen of its own
 * rather than in a save that fails.
 *
 * WHICH MEANS THIS ROUTE FETCHES THE EVENT ITSELF rather than calling
 * `loadCollection`. The loader returns a parsed collection and deliberately not
 * the raw event, because a reader has no business with the tags. An editor does:
 * the owner's envelope is a tag, and it is the only route to the key.
 *
 * THE ADDRESS ASKED FOR IS STILL AUTHORITATIVE OVER THE RELAY'S ANSWER. That
 * check is not inherited by fetching the event directly, so it is made again
 * here. An `naddr` carries relay hints chosen by whoever produced the link, so
 * an unchecked answer could load somebody else's collection into this author's
 * editor and invite them to save it over their own address.
 *
 * ---
 *
 * WHAT IT REFUSES AND SAYS ALOUD: somebody else's site, an address that is not a
 * site, and a site whose key this person does not hold. This is the writing
 * side, where an author is entitled to the real reason. The reading side's
 * discipline of collapsing every failure into one answer exists to protect a
 * private page's existence, and there is nothing to protect here: the person
 * asking is signed in and is asking about their own work.
 */

import type { ContentKey, SignerInterface } from '@cloistr/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  CollectionEditor,
  emptyCollectionDraft,
  type CollectionEditorDraft,
} from '@/components/CollectionEditor';
import { RequireSigner } from '@/components/RequireSigner';
import { decodeIdentifier } from '@/services/nostr/identifiers';
import { encodeAddress } from '@/services/nostr/pageLinks';
import { fetchNewest, relaysFor } from '@/services/nostr/relays';
import { parseCollectionEvent } from '@/services/pages/collectionEvent';
import { readOwnerKey } from '@/services/pages/collectionKeys';
import { PAGE_COLLECTION_KIND } from '@/services/pages/kinds';
import type { CollectionAddress } from '@/services/pages/publishCollection';

export function CollectionEditorRoute() {
  const params = useParams();

  // CAPTURED ONCE, as the other two editors do. Publishing a new site rewrites
  // the URL to that site's own address so a reload comes back to it; re-reading
  // the parameter would treat our own rewrite as a navigation and reload the
  // editor out from under the author a moment after they pressed Publish.
  const [naddr] = useState<string | null>(params.naddr ?? null);

  return (
    <RequireSigner title="Make a site" callToAction="Sign in to make or edit a site.">
      {(signer, pubkey) => <Editing naddr={naddr} signer={signer} pubkey={pubkey} />}
    </RequireSigner>
  );
}

type Loaded =
  | { status: 'new' }
  | { status: 'ready'; draft: CollectionEditorDraft; address: CollectionAddress }
  | { status: 'unavailable' }
  | { status: 'not-yours' }
  | { status: 'not-a-site' }
  | { status: 'no-key' };

function Editing({
  naddr,
  signer,
  pubkey,
}: {
  naddr: string | null;
  signer: SignerInterface;
  pubkey: string;
}) {
  const [loaded, setLoaded] = useState<Loaded | null>(naddr === null ? { status: 'new' } : null);

  /**
   * READ THROUGH A REF AND KEYED ON THE PUBKEY, never on the signer object. Once
   * a session is restored, `@cloistr/auth` is in a self-sustaining effect loop
   * that mints a new signer on every turn of it, so an effect depending on the
   * object re-runs forever -- and this one opens a relay subscription and asks
   * the signer to decrypt. A pubkey is a string and survives the churn.
   */
  const signerRef = useRef(signer);
  signerRef.current = signer;

  useEffect(() => {
    if (naddr === null) return;
    let live = true;

    // WRAPPED, BECAUSE THIS THROWS FOR AN nsec. A secret key in the URL is the
    // worst thing that can be in one, and the decoder refuses it loudly rather
    // than returning null. Caught here and collapsed to unavailable so the route
    // never echoes it into a message, a query or a retry.
    let decoded;
    try {
      decoded = decodeIdentifier(naddr);
    } catch {
      setLoaded({ status: 'unavailable' });
      return;
    }
    if (!decoded || decoded.type !== 'address') {
      setLoaded({ status: 'unavailable' });
      return;
    }
    // REFUSED BEFORE THE QUERY. An address naming another addressable kind is a
    // real reference to something real that is not a site, and opening it here
    // would offer to overwrite it with one.
    if (decoded.kind !== PAGE_COLLECTION_KIND) {
      setLoaded({ status: 'not-a-site' });
      return;
    }
    if (decoded.pubkey !== pubkey) {
      setLoaded({ status: 'not-yours' });
      return;
    }

    const address: CollectionAddress = {
      kind: PAGE_COLLECTION_KIND,
      pubkey: decoded.pubkey,
      identifier: decoded.identifier,
    };
    const coordinate = `${address.kind}:${address.pubkey}:${address.identifier}`;
    const relays = relaysFor(decoded.relays);

    void (async () => {
      const event = await fetchNewest(relays, {
        kinds: [PAGE_COLLECTION_KIND],
        authors: [address.pubkey],
        '#d': [address.identifier],
      });
      if (!live) return;
      if (!event) {
        setLoaded({ status: 'unavailable' });
        return;
      }

      // The address asked for beats the answer that came back. See the header.
      const identifier = event.tags.find((t) => t[0] === 'd')?.[1];
      if (event.pubkey !== address.pubkey || identifier !== address.identifier) {
        setLoaded({ status: 'unavailable' });
        return;
      }

      let key: ContentKey | null = null;
      try {
        key = await readOwnerKey(event.tags, coordinate, signerRef.current);
      } catch {
        // A key tag that is present and will not open means this signer is not
        // the one it was wrapped to. Distinct from there being no tag at all,
        // and both end in the same refusal below, because both mean this person
        // cannot save this site.
        key = null;
      }
      if (!live) return;
      if (!key) {
        setLoaded({ status: 'no-key' });
        return;
      }

      const collection = parseCollectionEvent(event, key);
      if (!collection) {
        setLoaded({ status: 'unavailable' });
        return;
      }

      setLoaded({
        status: 'ready',
        address,
        draft: {
          identifier: collection.identifier,
          title: collection.title,
          summary: collection.summary ?? '',
          type: collection.type ?? '',
          members: collection.members,
          front: collection.front ?? null,
          theme: collection.theme ?? null,
          key,
          // OUT OF THE SEALED HALF THE ROUTE JUST OPENED, so editing a
          // forty-page site asks the signer for nothing. Dropping them here
          // would republish the site without its members' keys and silently
          // lock out everybody it had been shared with, which is the same
          // failure as republishing only the fields that have inputs.
          memberKeys: collection.memberKeys,
        },
      });
    })();

    return () => {
      live = false;
    };
  }, [naddr, pubkey]);

  const onPublished = useCallback((address: CollectionAddress) => {
    // The history API rather than the router, so this does not remount the
    // editor and discard the list that is still on screen. It only makes the URL
    // describe the state the screen is already in.
    window.history.replaceState(null, '', `/site/${encodeAddress(address)}`);
  }, []);

  if (loaded === null) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  if (loaded.status === 'unavailable') {
    return (
      <div className="page-unavailable">
        <h1>That site is not available</h1>
        <p className="page-unavailable-note">
          Nothing at that address could be opened for editing.{' '}
          <Link to="/site">Start a new site</Link> instead.
        </p>
      </div>
    );
  }

  if (loaded.status === 'not-yours') {
    return (
      <div className="page-unavailable">
        <h1>That site is somebody else&rsquo;s</h1>
        <p className="page-unavailable-note">
          A site can only be edited by the key that published it. You can still put their pages
          in a site of your own: a page is referenced by address, and anyone may list it.{' '}
          <Link to="/site">Start a site of your own</Link>.
        </p>
      </div>
    );
  }

  if (loaded.status === 'not-a-site') {
    return (
      <div className="page-unavailable">
        <h1>That address is not a site</h1>
        <p className="page-unavailable-note">
          It names an event of another kind. Saving it here would replace it with a site.{' '}
          <Link to="/site">Start a new site</Link>.
        </p>
      </div>
    );
  }

  if (loaded.status === 'no-key') {
    return (
      <div className="page-unavailable">
        <h1>This site cannot be edited from here</h1>
        <p className="page-unavailable-note">
          It is signed by your key, and the copy of its key that should be attached to it will
          not open with the signer you are using. Saving it anyway would seal it under a new key
          and silently cut off everybody you had shared it with, so the editor will not open at
          all rather than let you arrange a site you cannot save.
        </p>
        <p className="page-unavailable-note">
          If you published this site with a different signer, use that one.{' '}
          <Link to="/site">Start a new site</Link> otherwise.
        </p>
      </div>
    );
  }

  return (
    <CollectionEditor
      signer={signer}
      initial={loaded.status === 'new' ? emptyCollectionDraft() : loaded.draft}
      initialAddress={loaded.status === 'new' ? null : loaded.address}
      onPublished={onPublished}
    />
  );
}
